import React, { useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import axios from "axios";
import VideoPlayer from "./components/VideoPlayer";
import RoomControls from "./components/RoomControls";
import mediasoupService from "../../services/mediasoupServices.js"; // Import the service
import "./projector.css";

export default function Projector() {
  const { enqueueSnackbar } = useSnackbar();
  const [roomId, setRoomId] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState([]);
  const [participants, setParticipants] = useState([]);
  const localVideoRef = useRef(null);

  const createRoom = async () => {
    try {
      // Create room through mediasoup service
      const newRoomId = await mediasoupService.createRoom();
      setRoomId(newRoomId);

      enqueueSnackbar(`Room created with ID: ${newRoomId}`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to create room", {
        variant: "error",
      });
    }
  };

  const startStreaming = async () => {
    try {
      // Initialize connection if not already done
      if (!mediasoupService.socket) {
        await mediasoupService.init({
          serverUrl: process.env.REACT_APP_SERVER_SCRIPT_HOST_SOCKET_IO,
        });
        await mediasoupService.joinRoom(roomId);
      }

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      // Display local video
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Start producing media
      await mediasoupService.produce(stream);

      setIsStreaming(true);
      setLocalStream(stream);
    } catch (error) {
      console.error("Streaming error:", error);
      enqueueSnackbar(error.message || "Failed to start streaming", {
        variant: "error",
      });
    }
  };

  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      // Stop previous stream if exists
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }

      // Display screen share
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Switch to screen share in MediaSoup
      await mediasoupService.switchToScreenShare(stream);

      setLocalStream(stream);
      enqueueSnackbar("Screen sharing started", { variant: "success" });
    } catch (error) {
      if (error.name !== "NotAllowedError") {
        enqueueSnackbar(error.message || "Screen share failed", {
          variant: "error",
        });
      }
    }
  };

  const stopStreaming = async () => {
    try {
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
        await mediasoupService.stopProducing();
        setLocalStream(null);
        setIsStreaming(false);

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = null;
        }
      }
    } catch (error) {
      console.error("Stop streaming error:", error);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        await mediasoupService.init({
          serverUrl: process.env.REACT_APP_SERVER_SCRIPT_HOST_SOCKET_IO,
        });

        if (mounted) {
          console.log("Socket successfully initialized");
        }
      } catch (error) {
        if (mounted) {
          enqueueSnackbar(`Connection failed: ${error.message}`, {
            variant: "error",
          });
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      mediasoupService.leaveRoom();
    };
  }, []);
  return (
    <div className="projector-host-page">
      <h1>Host a Streaming Session</h1>

      {!roomId ? (
        <button onClick={createRoom} className="create-room-btn">
          Create Room
        </button>
      ) : (
        <div className="room-container">
          <div className="room-info">
            <p>Room ID: {roomId}</p>
            <p>Share this ID with participants</p>
          </div>

          <div className="video-container">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="local-video"
            />
          </div>

          <div className="stream-controls">
            {!isStreaming ? (
              <>
                <button onClick={startStreaming}>Start Camera</button>
                <button onClick={startScreenShare}>Share Screen</button>
              </>
            ) : (
              <>
                <button onClick={stopStreaming}>Stop Streaming</button>
                <button onClick={startScreenShare}>
                  Switch to Screen Share
                </button>
              </>
            )}
          </div>

          <RoomControls
            participants={participants}
            onMuteAll={() => mediasoupService.muteAll()}
            onKickParticipant={(id) => mediasoupService.kickParticipant(id)}
          />
        </div>
      )}
    </div>
  );
}
